import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { pdfjs, Document, Page, PDFPageProxy } from 'react-pdf';
import { TextInput, InlineLoading, Button } from 'carbon-components-react';
import {
  ArrowLeft16,
  ArrowRight16,
  Add20,
  Subtract20,
  Download20
} from '@carbon/icons-react';
import { isIOS } from 'react-device-detect';
import {
  getAttachmentPreviewUrlStart,
  resetAttachmentPreviewUrl,
  getAttachmentDownloadUrlStart,
  resetAttachmentDownloadUrl
} from 'redux/enquiries/actions';
import {
  selectAttachmentPreviewUrl,
  selectLoadingAttachmentPreviewUrl,
  selectAttachmentDownloadUrl,
  selectLoadingAttachmentDownloadUrl
} from 'redux/enquiries';
import { getEmailAttachment, debounce } from 'utilities/helper.util';
import { useComponentMount } from 'utilities/hooks.util';
import { usePDFPasswordHandler } from 'customHooks/usePDFPasswordHandler';
import {
  initializePDFSecurity,
  cleanupPDFSecurity
} from 'utilities/pdf-security.util';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/2.12.313/pdf.worker.js`;
const maxScale = 5;

const PDFViewer: React.FC<Props> = ({
  fileId,
  enquiryId,
  fileName,
  isSuperModal = false,
  viewerContentHeight
}) => {
  const history = useHistory();
  const queryParams = new URLSearchParams(history?.location.search);
  const shareId = queryParams.get('shareId') || '';

  const dispatch = useDispatch();
  const isLoadingPreviewUrl: any = useSelector(
    selectLoadingAttachmentPreviewUrl
  );
  const previewUrl: any = useSelector(selectAttachmentPreviewUrl);
  const isLoadingDownloadUrl: any = useSelector(
    selectLoadingAttachmentDownloadUrl
  );
  const downloadUrl: any = useSelector(selectAttachmentDownloadUrl);
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageNumberToDisplay, setPageNumberToDisplay] = useState<number>(1);
  const [scale, setScale] = useState<number>(1);
  const [scalePercentage, setScalePercentage] = useState<number>(100);
  const [pageWidth, setPageWidth] = useState<number | undefined>(undefined);
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const [downloadButtonClicked, setDownloadButtonClicked] = useState<boolean>(
    false
  );

  const {
    state: passwordState,
    actions: passwordActions
  } = usePDFPasswordHandler();

  useComponentMount(() => {
    dispatch(resetAttachmentPreviewUrl());
    dispatch(resetAttachmentDownloadUrl());
  });

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPageNumber(1);
    setPageNumberToDisplay(1);
    setScalePercentage(100);
    setScale(1);
    passwordActions.handleDocumentLoadSuccess();
  };

  const onDocumentLoadError = (error: any) => {
    passwordActions.handleDocumentLoadError(error);
    console.error('PDF load error:', error);
  };

  const handleRetryPassword = () => {
    setNumPages(0);
    passwordActions.handleRetryPassword();
  };

  const onPageLoadSuccess = (page: PDFPageProxy) => {
    if (isIOS) {
      const { originalHeight, originalWidth } = page;
      const maxAreaLimit = 16777216;
      const pixelRatio = window.devicePixelRatio || 1;
      const areaAfterZoom =
        originalHeight *
        pixelRatio *
        maxScale *
        (originalWidth * pixelRatio * maxScale);
      if (areaAfterZoom > maxAreaLimit) {
        const ratio = originalWidth / originalHeight;
        const adjustedWidth =
          Math.sqrt(maxAreaLimit / ratio) / (pixelRatio * maxScale);
        setPageWidth(Number(adjustedWidth.toFixed(2)));
      }
    }

    // Initialize PDF annotation security after page loads
    setTimeout(() => {
      const annotationLayer = document.querySelector('.annotationLayer');
      if (annotationLayer) {
        initializePDFSecurity(annotationLayer);
      }
    }, 100); // Small delay to ensure annotation layer is rendered
  };

  const goPrevious = () => {
    if (pageNumber !== 1) {
      const pgNumber = pageNumber - 1;
      setPageNumber(pgNumber);
      setPageNumberToDisplay(pgNumber);
    }
  };

  const goNext = () => {
    if (pageNumber !== numPages) {
      const pgNumber = pageNumber + 1;
      setPageNumber(pgNumber);
      setPageNumberToDisplay(pgNumber);
    }
  };

  const zoomIn = () => {
    if (scale < maxScale) {
      const newScale = scale + 0.25;
      setScale(newScale);
      setScalePercentage(Math.trunc(newScale * 100));
    }
  };

  const zoomOut = () => {
    if (scale > 0) {
      const newScale = scale - 0.25;
      setScale(newScale);
      setScalePercentage(Math.trunc(newScale * 100));
    }
  };

  const downloadFile = () => {
    setDownloadButtonClicked(true);
    dispatch(getAttachmentDownloadUrlStart({ fileId, shareId, enquiryId }));
  };

  const handleScaleBlur = (value: string) => {
    if (value && Number(value)) {
      let valueInteger = Number(value);
      if (Number(value) > maxScale * 100) {
        valueInteger = maxScale * 100;
      } else if (Number(value) <= 0) {
        valueInteger = 1;
      }
      const newScale = Number(valueInteger / 100);
      setScale(newScale);
      setScalePercentage(valueInteger);
    }
  };

  const debouncedScaleChange = useCallback(
    debounce(async (args: string[]) => {
      handleScaleBlur(args[0]);
    }, 500),
    []
  );

  const handleScaleChange = (value: string) => {
    if (value && Number(value)) {
      setScalePercentage(Number(value));
      debouncedScaleChange(value);
    }
  };

  const handlePageBlur = (
    value: string,
    pageNum: string,
    totalPages: string
  ) => {
    const pageNumber = Number(pageNum);
    const numPages = Number(totalPages);
    if (value && Number(value)) {
      if (pageNumber !== Number(value)) {
        if (Number(value) < 0) {
          setPageNumber(1);
          setPageNumberToDisplay(1);
        } else if (Number(value) > numPages) {
          setPageNumber(numPages);
          setPageNumberToDisplay(numPages);
        } else {
          setPageNumber(Number(value));
          setPageNumberToDisplay(Number(value));
        }
      }
    } else {
      setPageNumber(1);
      setPageNumberToDisplay(1);
    }
  };

  const debouncedPageChange = useCallback(
    debounce(async (args: string[]) => {
      handlePageBlur(args[0], args[1], args[2]);
    }, 500),
    []
  );

  const handlePageChange = (value: string) => {
    if (value && Number(value)) {
      setPageNumberToDisplay(Number(value));
      debouncedPageChange(value, pageNumber, numPages);
    }
  };

  const CustomLoadingMessage = () => (
    <div
      style={{
        display: 'flex',
        border: '0px red solid',
        height: `calc(${viewerContentHeight} - 3rem)`,
        minHeight: '24rem',
        justifyContent: 'center',
        alignItems: 'center'
      }}
    >
      <InlineLoading description="Preparing response view ..." />
    </div>
  );

  const ErrorComponent = () => (
    <div
      className="pdf-viewer-error-container"
      style={{
        height: `calc(${viewerContentHeight} - 3rem)`,
        minHeight: '24rem'
      }}
    >
      <div className="pdf-viewer-error-message">
        {passwordState.passwordCancelled
          ? 'Password required to view this PDF document'
          : 'Failed to load PDF document'}
      </div>
      {passwordState.passwordCancelled && (
        <div className="pdf-viewer-error-button-container">
          <Button onClick={handleRetryPassword} kind="secondary">
            Enter password
          </Button>
        </div>
      )}
    </div>
  );

  // Reset states when pdfUrl changes
  useEffect(() => {
    if (pdfUrl) {
      passwordActions.resetStatesOnly();
    }
  }, [pdfUrl, passwordActions]);

  useEffect(() => {
    if (!isLoadingDownloadUrl && downloadUrl?.url && downloadButtonClicked) {
      getEmailAttachment({ url: downloadUrl.url, attachmentName: fileName });
      setTimeout(() => {
        setDownloadButtonClicked(false);
      }, 2000);
    }
  }, [isLoadingDownloadUrl, downloadUrl, downloadButtonClicked, fileName]);

  useEffect(() => {
    if (previewUrl && !isLoadingPreviewUrl) {
      setPdfUrl(previewUrl.url);
    }
  }, [isLoadingPreviewUrl, previewUrl]);

  useEffect(() => {
    if (fileId && fileId > 0) {
      dispatch(getAttachmentPreviewUrlStart({ fileId, shareId, enquiryId }));
    }
  }, [fileId, shareId, dispatch, enquiryId]);

  // Cleanup PDF security handlers when page changes or component unmounts
  useEffect(() => {
    return () => {
      cleanupPDFSecurity();
    };
  }, [pageNumber]);

  return (
    <>
      {numPages > 0 && (
        <div
          className={`pdf-viewer-header ${
            isSuperModal ? 'supermodal-header' : ''
          }`}
        >
          <div className="pdf-viewer-header-file-name">{fileName}</div>
          <div className="pdf-viewer-header-controls">
            <div
              className="pdf-viewer-header-controls-btn"
              onClick={() => goPrevious()}
            >
              <ArrowLeft16 />
            </div>
            <div className="pdf-viewer-header-controls-pages">
              <div>
                <TextInput
                  id="pages"
                  name="input-pages"
                  className="input-pages"
                  size="sm"
                  placeholder=""
                  labelText=""
                  onChange={(evt) => handlePageChange(evt.target.value)}
                  value={pageNumberToDisplay}
                />
              </div>
              <div className="input-pages-total">/{numPages}</div>
            </div>
            <div
              className="pdf-viewer-header-controls-btn"
              onClick={() => goNext()}
            >
              <ArrowRight16 />
            </div>
            <div className="pdf-viewer-header-controls-divider" />
            <div
              className="pdf-viewer-header-controls-btn"
              onClick={() => zoomOut()}
            >
              <Subtract20 />
            </div>
            <div className="pdf-viewer-header-controls-input">
              <TextInput
                id="scale"
                name="input-scale"
                className="input-scale"
                size="sm"
                placeholder=""
                labelText=""
                onChange={(evt) => handleScaleChange(evt.target.value)}
                value={scalePercentage}
              />{' '}
              <div className="input-percentage">%</div>
            </div>
            <div
              className="pdf-viewer-header-controls-btn"
              onClick={() => zoomIn()}
            >
              <Add20 />
            </div>
            <div className="pdf-viewer-header-controls-divider" />
            <div
              title={'Download pdf'}
              className={`pdf-viewer-header-controls-btn ${
                downloadButtonClicked
                  ? 'pdf-viewer-header-controls-btn-disabled'
                  : ''
              }`}
              onClick={() => downloadFile()}
            >
              <Download20 />
            </div>
          </div>
        </div>
      )}
      {!isLoadingPreviewUrl && pdfUrl ? (
        passwordState.hasError ? (
          <ErrorComponent />
        ) : (
          <div className="pdf-viewer-container">
            <Document
              key={passwordState.documentKey}
              className="pdf-viewer-document"
              file={pdfUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              onPassword={passwordActions.handlePassword}
              loading={<CustomLoadingMessage />}
            >
              <Page
                pageNumber={pageNumber}
                scale={scale}
                width={pageWidth}
                onLoadSuccess={onPageLoadSuccess}
                renderAnnotationLayer={true}
              />
            </Document>
          </div>
        )
      ) : (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <CustomLoadingMessage />
        </div>
      )}
    </>
  );
};

type Props = {
  fileId?: number;
  enquiryId?: number;
  fileUrl: string;
  fileName: string;
  isSuperModal?: boolean;
  viewerContentHeight?: any;
};

export default React.memo(PDFViewer);
